import { Product } from '../../types/Product';
import { Insight } from '../../types/Statistics';
import { 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  Percent, 
  Tag,
  Scale
} from 'lucide-react';

export const calculateInsights = (products: Product[]): Insight[] => {
  // Calculate price insights
  const averagePrice = products.reduce((sum, p) => sum + p.current_price, 0) / products.length;
  const discountedProducts = products.filter(p => p.price_decreased);
  const totalSavings = discountedProducts.reduce(
    (sum, p) => sum + (p.previous_price - p.current_price), 
    0
  );

  // Calculate category insights
  const categoryStats = products.reduce((acc, product) => {
    const category = product.category_name;
    if (!acc[category]) {
      acc[category] = {
        count: 0,
        totalPrice: 0,
        discounted: 0
      };
    }
    acc[category].count++;
    acc[category].totalPrice += product.current_price;
    if (product.price_decreased) acc[category].discounted++;
    return acc;
  }, {} as Record<string, { count: number; totalPrice: number; discounted: number }>);

  // Find most and least expensive categories
  const categoryPrices = Object.entries(categoryStats).map(([category, stats]) => ({
    category,
    avgPrice: stats.totalPrice / stats.count
  }));

  const mostExpensive = categoryPrices
    .sort((a, b) => b.avgPrice - a.avgPrice)
    .slice(0, 3);

  const mostDiscounted = Object.entries(categoryStats)
    .map(([category, stats]) => ({
      category,
      discountRate: (stats.discounted / stats.count) * 100
    }))
    .sort((a, b) => b.discountRate - a.discountRate)
    .slice(0, 3);

  return [
    {
      title: 'Análisis de Precios',
      description: 'Resumen de precios y tendencias',
      icon: DollarSign,
      color: 'bg-green-500',
      metrics: [
        {
          label: 'Precio promedio',
          value: `${averagePrice.toFixed(2)}€`,
          highlight: true
        },
        {
          label: 'Ahorro total disponible',
          value: `${totalSavings.toFixed(2)}€`,
          highlight: true
        }
      ]
    },
    {
      title: 'Categorías Premium',
      description: 'Categorías con precios más elevados',
      icon: Tag,
      color: 'bg-purple-500',
      metrics: mostExpensive.map(({ category, avgPrice }) => ({
        label: category,
        value: `${avgPrice.toFixed(2)}€`,
        highlight: true
      }))
    },
    {
      title: 'Mejores Ofertas',
      description: 'Categorías con más descuentos',
      icon: Percent,
      color: 'bg-blue-500',
      metrics: mostDiscounted.map(({ category, discountRate }) => ({
        label: category,
        value: `${discountRate.toFixed(1)}%`,
        highlight: true
      }))
    },
    {
      title: 'Oportunidades de Ahorro',
      description: 'Productos con mayores descuentos',
      icon: Scale,
      color: 'bg-orange-500',
      metrics: discountedProducts
        .sort((a, b) => 
          (b.previous_price - b.current_price) - (a.previous_price - a.current_price)
        )
        .slice(0, 3)
        .map(product => ({
          label: product.display_name,
          value: `${(product.previous_price - product.current_price).toFixed(2)}€`,
          highlight: true
        }))
    }
  ];
};